import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { LOGO_SRC } from '../../utils/assets';

function storeRect(img) {
  const r = img.getBoundingClientRect();
  sessionStorage.setItem('splash_logo_rect', JSON.stringify({
    left: r.left, top: r.top, width: r.width, height: r.height,
  }));
}

// Full-screen branded splash shown at session start (HomeRedirect) and as the
// lazy-route loading fallback. Pass captureRect to store the logo rect in
// sessionStorage for the Header FLIP — only HomeRedirect should pass this,
// not the Suspense fallback, which fires on every lazy-route navigation.
//
// Two timing races handled when captureRect is true:
//   Reload (assets cached): useLayoutEffect wins the race before microtasks. ✓
//   First load (assets from network): useEffect load-event fallback handles it. ✓
//
// It *is* the Suspense fallback, so it cannot suspend: `common` only.
const SplashScreen = ({ captureRect = false }) => {
  const { t } = useTranslation('common');
  const logoRef = useRef(null);

  useLayoutEffect(() => {
    if (!captureRect) return;
    const img = logoRef.current;
    if (img?.complete && img.naturalWidth) storeRect(img);
  }, [captureRect]);

  useEffect(() => {
    if (!captureRect) return;
    const img = logoRef.current;
    if (!img) return;
    if (img.complete && img.naturalWidth) return; // useLayoutEffect already handled this
    const onLoad = () => storeRect(img);
    img.addEventListener('load', onLoad);
    return () => img.removeEventListener('load', onLoad);
  }, [captureRect]);

  return (
    <div
      role="status"
      aria-label={t('loading')}
      className="fixed inset-0 z-[200] bg-applauss flex items-center justify-center"
    >
      <img
        ref={logoRef}
        src={LOGO_SRC}
        alt="Applauss"
        className={`h-32 w-auto mx-auto${captureRect ? '' : ' animate-pulse'}`}
      />
    </div>
  );
};

export default SplashScreen;
